<template>
  <div class="bg-page flex flex-col items-center w-full">

    <!-- ── HERO ── -->
    <section class="flex flex-col items-center w-full px-4 md:px-0 pt-10 md:pt-16 gap-10">

      <!-- Heading block -->
      <div class="flex flex-col items-center text-center gap-4 pt-12 md:pt-16 max-w-2xl">
        <h1 class="font-serif font-normal text-brand text-[32px] md:text-5xl leading-[36px] md:leading-[56px]">
          Water as <em>a way</em><br>of living.
        </h1>
        <p class="font-sans font-light text-muted text-sm md:text-base leading-relaxed max-w-xl">
          We are a small studio built around one obsession — the way water shapes landscape, culture, and the body. Everything we make begins there.
        </p>
      </div>

      <!-- 12:9 Looping Video — centered, 80% width on desktop -->
      <div class="w-full overflow-hidden md:px-4">
        <video
          autoplay
          loop
          muted
          playsinline
          preload="metadata"
          class="w-full block pointer-events-none"
        >
          <source src="/videos/about-hero.mp4" type="video/mp4" />
        </video>
      </div>

      <!-- Video caption -->
      <p class="font-label font-light text-muted tracking-[1px] text-[10px]  mb-14">
        SALTON SEA, CALIFORNIA
      </p>

    </section>

    <!-- ── BODY COPY ── -->
    <div class="flex flex-col w-full md:w-4/6 mx-auto px-4 md:px-0 pb-24 gap-10">

      <section class="flex flex-col gap-4">
        <h2 class="font-serif font-normal text-dark text-2xl md:text-[28px]">Where it began</h2>
        <p class="font-sans font-light text-secondary text-sm md:text-base leading-relaxed">
          Wet Water started as a question, asked somewhere between the Salton Sea and the California coast on a very long drive: why does water mean so many different things depending on where you are standing?
        </p>
        <p class="font-sans font-light text-secondary text-sm md:text-base leading-relaxed">
          In the desert, it is survival. On the coast, it is abundance. In the mountains, it is patience — snowpack held in trust until the season decides to release it. We became interested in that full range of meaning, and in the people who live closest to its edges.
        </p>
        <p class="font-sans font-light text-secondary text-sm md:text-base leading-relaxed">
          From that curiosity, we built a brand. A small line of water products — hydration, electrolytes, mineral supplements — made with the same intention we bring to everything: sourced carefully, formulated honestly, and presented without noise.
        </p>
      </section>

      <div class="bg-stroke h-px w-full" />

      <blockquote class="border-l-2 border-brand pl-5 font-serif font-normal italic text-dark text-lg md:text-xl leading-relaxed">
        "The desert teaches you what water actually is. Everything else is just drinking."
      </blockquote>

      <div class="bg-stroke h-px w-full" />

      <section class="flex flex-col gap-4">
        <h2 class="font-serif font-normal text-dark text-2xl md:text-[28px]">The journal</h2>
        <p class="font-sans font-light text-secondary text-sm md:text-base leading-relaxed">
          Alongside our products, we publish a journal. It is where we do our real thinking — long-form stories about place, drought, swimming holes, the rituals people build around water in different corners of the American West.
        </p>
        <p class="font-sans font-light text-secondary text-sm md:text-base leading-relaxed">
          The journal is not a blog. It does not move fast. We publish one story a week, written slowly, photographed carefully, and edited until the only thing left is the part that matters. We are interested in the texture of places and the people who know them well.
        </p>
        <p class="font-sans font-light text-secondary text-sm md:text-base leading-relaxed">
          If you have been here before, you may have read Marisol walking the edge of the reservoir at dusk, or Rafael mixing something cool in the kitchen of a high-desert rental. Those are our recurring characters — fictional, but drawn from real landscapes and real people we have spent time with.
        </p>
      </section>

      <div class="bg-stroke h-px w-full" />

      <section class="flex flex-col gap-4">
        <h2 class="font-serif font-normal text-dark text-2xl md:text-[28px]">What we believe</h2>
        <p class="font-sans font-light text-secondary text-sm md:text-base leading-relaxed">
          We believe that good products and good stories belong together. That the best things are made slowly and sold without urgency. That the American West is not a backdrop — it is a subject worth paying close attention to.
        </p>
        <p class="font-sans font-light text-secondary text-sm md:text-base leading-relaxed">
          We believe in small runs, honest ingredients, and writing that treats the reader as a serious person. We are not interested in optimization, hustle, or the language of wellness. We are interested in the actual feeling of drinking cold water after a long walk on a hot day.
        </p>
        <p class="font-sans font-light text-secondary text-sm md:text-base leading-relaxed">
          That is what Wet Water is for.
        </p>
      </section>

    </div>

    <!-- ── VALUES ── -->
    <section class="w-full bg-dark py-16 md:py-24 px-4">
      <div class="max-w-5xl mx-auto flex flex-col gap-10">
        <p class="font-label font-light text-brand tracking-[2px] text-xs md:text-sm">
          OUR PRINCIPLES
        </p>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          <div
            v-for="value in values"
            :key="value.title"
            class="flex flex-col gap-2"
          >
            <h3 class="font-serif font-normal text-inverse text-lg">{{ value.title }}</h3>
            <p class="font-sans font-light text-muted text-sm leading-relaxed">{{ value.body }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── FOUNDER ── -->
    <section class="w-full md:w-1/2 mx-auto px-4 md:px-0 py-16">
      <div class="flex flex-col gap-4">
        <div class="flex flex-row gap-4 md:gap-8 items-center">
          <img src="/images/jeff-avatar.webp" alt="Jeff" class="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover shrink-0" />
          <div class="flex flex-col gap-1">
            <p class="font-serif font-normal text-dark text-lg">Jeff Tiemann</p>
            <p class="font-label font-light text-brand tracking-[1.5px] text-[10px]">
              FOUNDER &amp; EDITORIAL DIRECTOR
            </p>
          </div>
        </div>
        <div class="bg-stroke h-px w-full" />
        <p class="font-sans font-light text-secondary text-sm leading-relaxed">
          Jeff grew up between the Mojave and the Pacific, which is probably where the obsession started. He spent a decade chasing the edges of the American West before deciding he wanted to make something you could actually drink. Wet Water is the result of that decision, made on a long drive with the windows down and the AC off.
        </p>
      </div>
    </section>

    <!-- ── CLOSING MARK ── -->
    <div class="w-full md:w-4/6 mx-auto px-4 md:px-0 pb-16">
      <div class="bg-stroke h-px w-full" />
      <p class="font-label font-light text-muted tracking-[2px] text-[10px] text-center mt-6">
        — WET WATER CO. —
      </p>
    </div>

  </div>
</template>

<script setup lang="ts">
useHead({
  title: 'About — Wet Water',
  meta: [
    {
      name: 'description',
      content: 'Wet Water is a small studio built around one obsession — the way water shapes landscape, culture, and the body.',
    },
  ],
})

const values = [
  {
    title: 'Place-driven',
    body: 'Every product and every story starts with a specific geography. We are rooted in the American Southwest and the California coast — not as aesthetic, but as lived experience.',
  },
  {
    title: 'Honest formulation',
    body: 'No proprietary blends, no filler ingredients, no inflated claims. We list what is in our products clearly and explain why it is there.',
  },
  {
    title: 'Slow publishing',
    body: 'One journal story per week, written with care and edited until nothing unnecessary remains. We would rather say one true thing than ten convenient ones.',
  },
  {
    title: 'Low volume, high intention',
    body: 'We run small batches. We do not warehouse inventory. When something is gone, we wait until we can make it right before making more.',
  },
  {
    title: 'No urgency',
    body: 'No countdown timers. No flash sales. No artificial scarcity. We trust that if something is good, it will find the people it belongs with.',
  },
  {
    title: 'Water first',
    body: 'Everything we make, write, and sell points back to the same obsession: water as a physical thing, a cultural force, and a daily practice worth paying attention to.',
  },
]
</script>